import { useMemo, useState } from 'react';
import moment from 'moment';
import { Loading } from '../components/Loading';
import { useAppContext } from '../contexts/AppContext';
import Dropdown from '../modules/agenda/estabelecimento/components/Dropdown';
import NotificationItem from './Item';
import { useNotificationsContext } from './NotificationsContext';
import { NotificationsService } from './Service';
import './Notifications.css';
import { Bell, Ellipsis } from 'lucide-react';

const service = new NotificationsService();

const sectionTitle = (createdAt: Date | string) => {
    const date = moment(createdAt);
    if (date.isSame(moment(), 'day')) return 'Hoje';
    if (date.isSame(moment().subtract(1, 'day'), 'day')) return 'Ontem';
    return 'Anteriores';
};

// Listagem unica do sino, do popover do menu e de /notificacoes.
export default function NotificationsList({ title }: { title?: string }) {
    const { errorSnackbar } = useAppContext();
    const {
        notifications,
        countNotVisualized,
        onlyNotVisualized,
        setOnlyNotVisualized,
        loadNotifications,
        loadMoreNotifications,
        hasMoreNotifications,
        loadingNotifications,
    } = useNotificationsContext();
    const [saving, setSaving] = useState(false);
    // Confirmacao no proprio painel: um dialogo em portal conta como clique fora e fecharia o popover.
    const [confirmingRemove, setConfirmingRemove] = useState(false);

    const sections = useMemo(() => {
        const groups: { title: string; items: typeof notifications }[] = [];
        notifications.forEach((notification) => {
            const name = sectionTitle(notification.createdAt);
            const current = groups.find((group) => group.title === name);
            if (current) current.items.push(notification);
            else groups.push({ title: name, items: [notification] });
        });
        return groups;
    }, [notifications]);

    const run = async (action: () => Promise<unknown>, errorMessage: string) => {
        setSaving(true);
        try {
            await action();
            await loadNotifications();
            setConfirmingRemove(false);
        } catch (error) {
            console.error(error);
            errorSnackbar(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const actionOptions = [
        ...(countNotVisualized > 0
            ? [
                  {
                      label: 'Marcar todas como lidas',
                      onClick: () => run(() => service.viewAll(), 'Não foi possível marcar as notificações como lidas.'),
                  },
              ]
            : []),
        ...(notifications.length > 0 || onlyNotVisualized
            ? [{ label: 'Remover todas', onClick: () => setConfirmingRemove(true) }]
            : []),
    ];

    return (
        <div className="sf-notif-list">
            {!!title && <h3 className="sf-notif-heading">{title}</h3>}

            <div className="sf-notif-toolbar">
                <div className="sf-segmented min-w-0 flex-1" role="group" aria-label="Filtrar notificações">
                    <button type="button" aria-pressed={!onlyNotVisualized} onClick={() => setOnlyNotVisualized(false)}>
                        Todas
                    </button>
                    <button type="button" aria-pressed={onlyNotVisualized} onClick={() => setOnlyNotVisualized(true)}>
                        Não lidas
                        {countNotVisualized > 0 && <span className="sf-segmented-count">{countNotVisualized}</span>}
                    </button>
                </div>
                {actionOptions.length > 0 && (
                    <Dropdown options={actionOptions}>
                        <button
                            type="button"
                            disabled={saving}
                            className="sf-icon-button btn btn-sm btn-circle"
                            aria-label="Mais ações"
                        >
                            <Ellipsis aria-hidden="true" />
                        </button>
                    </Dropdown>
                )}
            </div>

            {confirmingRemove && (
                <div className="sf-notif-confirm" role="alert">
                    <p>Remover todas as suas notificações? Isso não pode ser desfeito.</p>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={saving}
                            onClick={() => setConfirmingRemove(false)}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-error btn-sm"
                            disabled={saving}
                            onClick={() => run(() => service.removeAll(), 'Não foi possível remover as notificações.')}
                        >
                            Remover
                        </button>
                    </div>
                </div>
            )}

            {notifications.length === 0 ? (
                loadingNotifications ? (
                    <div className="flex justify-center py-10">
                        <Loading size="sm" />
                    </div>
                ) : (
                    <div className="sf-state">
                        <Bell aria-hidden="true" />
                        <h2>{onlyNotVisualized ? 'Nenhuma notificação não lida' : 'Nenhuma notificação'}</h2>
                        {!onlyNotVisualized && <p>Avisos sobre agendamentos e novidades aparecem aqui.</p>}
                    </div>
                )
            ) : (
                sections.map((section) => (
                    <section key={section.title} className="sf-notif-section">
                        <h4 className="sf-group-title">{section.title}</h4>
                        <ul className="sf-group">
                            {section.items.map((notification) => (
                                <NotificationItem key={notification._id} notification={notification} />
                            ))}
                        </ul>
                    </section>
                ))
            )}

            {hasMoreNotifications && (
                <div className="mt-3 flex justify-center">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={loadingNotifications}
                        onClick={() => {
                            void loadMoreNotifications();
                        }}
                    >
                        {loadingNotifications ? 'Carregando...' : 'Carregar mais'}
                    </button>
                </div>
            )}
        </div>
    );
}
